import { useEffect, useState } from "react";
import api, { currency, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_STYLES = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    processing: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    disbursed: "bg-komma-gold/15 text-komma-navy border-komma-gold/40",
    rejected: "bg-red-50 text-red-700 border-red-200",
    closed: "bg-gray-100 text-gray-700 border-gray-200",
};

const LOAN_TYPES = ["personal", "home", "business", "vehicle", "education", "gold"];
const STATUS_OPTIONS = ["new", "processing", "approved", "disbursed", "rejected", "closed"];

export default function Loans() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ customer_id: "", loan_type: "personal", amount: "", tenure_months: 36, interest_rate: 12, purpose: "" });
    const canApprove = ["ceo", "office_admin", "agent", "branch_manager"].includes(user.role);
    const canCreate = ["ceo", "office_admin", "marketing_executive", "branch_manager"].includes(user.role);

    const load = async () => {
        const [l, c] = await Promise.all([api.get("/loans"), api.get("/customers").catch(() => ({ data: [] }))]);
        setRows(l.data);
        setCustomers(c.data);
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/loans", {
                ...form,
                amount: Number(form.amount),
                tenure_months: Number(form.tenure_months),
                interest_rate: Number(form.interest_rate),
            });
            toast.success("Loan created");
            setOpen(false);
            setForm({ customer_id: "", loan_type: "personal", amount: "", tenure_months: 36, interest_rate: 12, purpose: "" });
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/loans/${id}/status`, { status });
            toast.success(`Loan ${status}`);
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6" data-testid="loans-page">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Portfolio</p>
                    <h1 className="font-serif text-3xl text-komma-navy">Loan Applications</h1>
                </div>
                {canCreate && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-komma-navy hover:bg-komma-navy-light" data-testid="add-loan-btn">
                                <Plus size={16} className="mr-2" /> New Application
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader><DialogTitle className="font-serif text-komma-navy">New Loan Application</DialogTitle></DialogHeader>
                            <form onSubmit={create} className="space-y-3">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Customer</Label>
                                    <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="loan-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                                        <SelectContent>
                                            {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Loan Type</Label>
                                        <Select value={form.loan_type} onValueChange={(v) => setForm({ ...form, loan_type: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="loan-type"><SelectValue /></SelectTrigger>
                                            <SelectContent>{LOAN_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Amount (₹)</Label>
                                        <Input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-10" data-testid="loan-amount" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Tenure (months)</Label>
                                        <Input type="number" required value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} className="mt-1.5 h-10" data-testid="loan-tenure" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Interest (% p.a.)</Label>
                                        <Input type="number" step="0.1" required value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} className="mt-1.5 h-10" data-testid="loan-rate" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Purpose</Label>
                                    <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="mt-1.5 h-10" data-testid="loan-purpose" />
                                </div>
                                <Button type="submit" className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="loan-submit">Create Application</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="komma-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-komma-navy/5">
                        <tr className="text-left">
                            {["Loan #", "Customer", "Type", "Amount", "EMI", "Status", canApprove ? "Action" : ""].filter(Boolean).map((h) => (
                                <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-widest text-komma-navy">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((l) => (
                            <tr key={l.id} className="border-t border-gray-100 hover:bg-komma-cream/50 transition-colors" data-testid={`loan-row-${l.id}`}>
                                <td className="px-4 py-3 font-mono text-xs text-komma-navy">
                                    <Link to={`/loans/${l.id}`} className="hover:text-komma-gold inline-flex items-center gap-1" data-testid={`loan-open-${l.id}`}>
                                        {l.loan_number} <ExternalLink size={11} />
                                    </Link>
                                    <Link to={`/loans/${l.id}/documents`} className="ml-2 text-[10px] uppercase tracking-widest text-komma-gold hover:underline" data-testid={`loan-docs-${l.id}`}>
                                        Docs
                                    </Link>
                                </td>
                                <td className="px-4 py-3 font-semibold text-komma-navy">{l.customer_name}</td>
                                <td className="px-4 py-3 capitalize">{l.loan_type}</td>
                                <td className="px-4 py-3 font-semibold">{currency(l.amount)}</td>
                                <td className="px-4 py-3 text-gray-600">{currency(l.emi)}</td>
                                <td className="px-4 py-3">
                                    <Badge variant="outline" className={`${STATUS_STYLES[l.status]} capitalize`}>{l.status}</Badge>
                                </td>
                                {canApprove && (
                                    <td className="px-4 py-3">
                                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                                            <SelectTrigger className="h-8 w-36 text-xs" data-testid={`loan-status-${l.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize text-xs">{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-500">No loans yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
